export function capitalizeWords(input: string): string {
  return input
    .toLowerCase() // Convertir todo el texto a minúsculas
    .split(" ") // Dividir la cadena en palabras
    .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalizar la primera letra de cada palabra
    .join(" "); // Unir las palabras nuevamente
}

export function normalizeString(input: string): string {
  // Elimina tildes y caracteres especiales, y pasa todo a minúsculas.
  return input
    .normalize("NFD") // Descompone caracteres con tildes
    .replace(/[\u0300-\u036f]/g, "") // Elimina las marcas diacríticas (tildes)
    .toLowerCase(); // Convierte a minúsculas
}

export function stringContainsAllWords(base: string, search: string): boolean {

  if (!base || base === "") {
    return false;
  }

  // Normaliza ambas cadenas para compararlas de forma consistente
  const normalizedBase = normalizeString(base);
  const searchWords = normalizeString(search).split(/\s+/); // Divide en palabras por espacios

  // Verifica que todas las palabras de 'search' estén en 'base'
  return searchWords.every(word => normalizedBase.includes(word));
}

export const parseLabel = (label: string) => {
  // RegEx para identificar patrones de markdown
  const regex = /(\*\*.*?\*\*|\*.*?\*|!.*?!)/g;
  const segments = label.split(regex); // Divide el texto en segmentos

  return segments.map((segment, index) => {
    if (/^\*\*(.*?)\*\*$/.test(segment)) {
      // Negrita
      return (
        <span key={ index } className="font-semibold">
          { segment.slice(2, -2) } {/* Elimina ** */ }
        </span>
      );
    } else if (/^\*(.*?)\*$/.test(segment)) {
      // Cursiva
      return (
        <span key={ index } className="font-semibold italic">
          { segment.slice(1, -1) } {/* Elimina * */ }
        </span>
      );
    } else if (/^!(.*?)!$/.test(segment)) {
      // Color primario
      return (
        <span key={ index } className="text-primary">
          { segment.slice(1, -1) } {/* Elimina ! */ }
        </span>
      );
    } else {
      // Texto normal
      return <span key={ index }>{ segment }</span>;
    }
  });
};

export const padWord = (
  word: string,
  targetLength: number,
  padChar: string = " ",
  padStart: boolean = true
): string => {
  if (padChar.length !== 1) {
    throw new Error("El carácter de relleno debe ser un único carácter.");
  }

  if (word.length >= targetLength) {
    return word;
  }

  const padding = padChar.repeat(targetLength - word.length);

  return padStart ? padding + word : word + padding;
};

export const generateLine = (char: string, length: number): string => {
  return char.repeat(length);
};

export function numberToWords(num: number): string {
  const unidades = [
    '', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve',
  ];
  // índice 0 = 10, índice 1 = 11, ... índice 9 = 19
  const especiales = [
    'diez', 'once', 'doce', 'trece', 'catorce', 'quince',
    'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve',
  ];
  const decenas = [
    '', 'diez', 'veinte', 'treinta', 'cuarenta', 'cincuenta',
    'sesenta', 'setenta', 'ochenta', 'noventa',
  ];
  // 'ciento' para combinaciones (101-199), caso exacto 100 = 'cien'
  const centenas = [
    '', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos',
    'seiscientos', 'setecientos', 'ochocientos', 'novecientos',
  ];

  if (num === 0) return 'cero';

  const entero = Math.floor(num);

  if (entero < 10) return unidades[entero];
  if (entero < 20) return especiales[entero - 10];
  if (entero < 100) {
    const decena = Math.floor(entero / 10);
    const unidad = entero % 10;
    return unidad === 0 ? decenas[decena] : `${ decenas[decena] } y ${ unidades[unidad] }`;
  }
  if (entero === 100) return 'cien';
  if (entero < 1000) {
    const centena = Math.floor(entero / 100);
    const resto = entero % 100;
    return resto === 0 ? centenas[centena] : `${ centenas[centena] } ${ numberToWords(resto) }`;
  }
  if (entero === 1000) return 'mil';
  if (entero < 2000) {
    const resto = entero % 1000;
    return resto === 0 ? 'mil' : `mil ${ numberToWords(resto) }`;
  }
  if (entero < 1000000) {
    const miles = Math.floor(entero / 1000);
    const resto = entero % 1000;
    const milesStr = `${ numberToWords(miles) } mil`;
    return resto === 0 ? milesStr : `${ milesStr } ${ numberToWords(resto) }`;
  }
  if (entero < 2000000) {
    const resto = entero % 1000000;
    return resto === 0 ? 'un millón' : `un millón ${ numberToWords(resto) }`;
  }
  if (entero < 1000000000) {
    const millones = Math.floor(entero / 1000000);
    const resto = entero % 1000000;
    const millonesStr = `${ numberToWords(millones) } millones`;
    return resto === 0 ? millonesStr : `${ millonesStr } ${ numberToWords(resto) }`;
  }

  return 'Número demasiado grande para convertir';
}

export function base64ToBlob(base64: string, contentType?: string): Blob {
  const [header, data] = base64.split(",");
  const detectedType = contentType || header.match(/data:(.*?);/)?.[1] || "image/jpg";
  const binaryData = atob(data); // Decodificar la cadena Base64
  const byteArray = Uint8Array.from(binaryData, char => char.charCodeAt(0)); // Convertir a ArrayBuffer

  return new Blob([ byteArray ], { type: detectedType });
}
